/**
 * Step 03 (automatic lane) — the reels we read (spec §2.2).
 *
 * The link input, the drop zone and the source list are the ones that have
 * been in the wizard since the study shipped; what changed is the frame around
 * them: Continue reads "Read my brand", and a missing source is a sentence at
 * the input rather than a dead button.
 */
import { useRef, useState, type Dispatch, type RefObject } from "react";
import { Film, Image as ImageIcon, Link2, Trash2, Upload } from "lucide-react";
import type { BrandStudyRecord, StudyFailure } from "../../../api";
import { MAX_SOURCES, type BrandSetupAction, type BrandSetupSource, type BrandSetupState } from "../../../lib/brandSetup/state";
import { Block, MONO_LABEL, REASON } from "../ui";
import { REASON_ID, useStepReason } from "../StepFrame";

const ACCEPT = ".mp4,.mov,.m4v,.webm,.png,.jpg,.jpeg,.webp";

/** The sentence the study wrote about THIS source, if it wrote one. */
function failureFor(source: BrandSetupSource, failures: StudyFailure[]): string | null {
  const hit = failures.find((f) =>
    f.source.kind === "url" && source.kind === "url"
      ? f.source.url === source.url
      : f.source.kind === "upload" && source.kind === "upload"
        ? f.source.uploadId === source.uploadId
        : false,
  );
  return hit?.message ?? null;
}

function sourceLabel(s: BrandSetupSource): string {
  return s.kind === "url" ? s.url : s.filename;
}

export function SourcesStep(props: {
  state: BrandSetupState;
  dispatch: Dispatch<BrandSetupAction>;
  record: BrandStudyRecord | null;
  uploading: { name: string; pct: number } | null;
  error: string | null;
  linkRef: RefObject<HTMLInputElement | null>;
  onAddFiles: (files: FileList) => void;
}): React.JSX.Element {
  const { state, dispatch, record, uploading, error, linkRef } = props;
  const reason = useStepReason();
  const [url, setUrl] = useState("");
  const [dropping, setDropping] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const full = state.sources.length >= MAX_SOURCES;
  const failures = record?.failures ?? [];

  const addUrl = (): void => {
    const v = url.trim();
    if (!v || full) return;
    dispatch({ type: "patch", patch: { sources: [...state.sources, { kind: "url", url: v }] } });
    setUrl("");
  };

  return (
    <div style={{ display: "grid", gap: 22 }}>
      <Block label="A link to the video">
        {/* data-no-enter: Return here ADDS the link — it must not skip the
            screen out from under a half-pasted URL. */}
        <div style={{ display: "flex", gap: 8 }} data-no-enter>
          <input
            ref={linkRef}
            data-autofocus
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addUrl();
              }
            }}
            placeholder="https://instagram.com/reel/…"
            aria-label="A link to the video"
            aria-describedby={reason ? REASON_ID : undefined}
            disabled={full}
            style={{
              flex: 1,
              minWidth: 0,
              padding: "12px 14px",
              borderRadius: 12,
              border: `1px solid ${reason ? "var(--ql-danger)" : "rgba(255,255,255,.10)"}`,
              background: "rgba(17,24,39,.5)",
              color: "var(--ql-fg)",
              fontSize: 14,
              fontFamily: "inherit",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
          <button
            type="button"
            onClick={addUrl}
            disabled={!url.trim() || full}
            style={{
              padding: "12px 18px",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,.12)",
              background: "rgba(255,255,255,.03)",
              color: "var(--ql-fg)",
              fontWeight: 700,
              fontSize: 13,
              fontFamily: "inherit",
              cursor: url.trim() && !full ? "pointer" : "not-allowed",
              opacity: url.trim() && !full ? 1 : 0.5,
            }}
          >
            Add
          </button>
        </div>
        {reason && (
          <div id={REASON_ID} style={{ ...REASON, marginTop: 10 }}>
            {reason}
          </div>
        )}
      </Block>

      <Block label="Or upload a video or a post" hint="MP4, MOV, WebM, PNG, JPG or WebP. Up to eight in total.">
        <label
          htmlFor="bs-assets"
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={(e) => {
            e.preventDefault();
            if (!full) setDropping(true);
          }}
          onDragLeave={() => setDropping(false)}
          onDrop={(e) => {
            // preventDefault FIRST: without it the browser opens the file and
            // the half-finished wizard is gone.
            e.preventDefault();
            setDropping(false);
            if (!full && e.dataTransfer.files.length) props.onAddFiles(e.dataTransfer.files);
          }}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            minHeight: 88,
            borderRadius: 14,
            border: dropping ? "2px dashed var(--ql-yellow)" : "2px dashed rgba(255,255,255,.14)",
            background: dropping ? "rgba(255,213,0,.06)" : "rgba(255,255,255,.015)",
            cursor: full ? "not-allowed" : "pointer",
            color: dropping ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
            fontSize: 13,
            opacity: full ? 0.5 : 1,
            transition: "all .15s var(--ql-ease)",
          }}
        >
          <Upload size={20} />
          <span>{uploading ? `Uploading ${uploading.name} — ${uploading.pct}%` : "Drop or click to upload"}</span>
          <input
            id="bs-assets"
            ref={fileRef}
            type="file"
            multiple
            accept={ACCEPT}
            disabled={full}
            style={{ display: "none" }}
            onChange={(e) => {
              if (e.target.files?.length) props.onAddFiles(e.target.files);
              if (fileRef.current) fileRef.current.value = "";
            }}
          />
        </label>
      </Block>

      {state.sources.length > 0 && (
        <div style={{ display: "grid", gap: 6 }}>
          <div style={MONO_LABEL}>
            {state.sources.length} of {MAX_SOURCES}
          </div>
          {state.sources.map((s, i) => {
            const failed = failureFor(s, failures);
            return (
              <div
                key={`${s.kind}-${i}-${sourceLabel(s)}`}
                style={{
                  display: "grid",
                  gap: 6,
                  padding: "9px 12px",
                  borderRadius: 11,
                  border: failed ? "1px solid rgba(248,113,113,.35)" : "1px solid rgba(255,255,255,.07)",
                  background: "rgba(255,255,255,.02)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ color: failed ? "var(--ql-danger)" : "var(--ql-fg-subtle)", flexShrink: 0, display: "inline-flex" }}>
                    {s.kind === "url" ? <Link2 size={14} /> : s.assetKind === "image" ? <ImageIcon size={14} /> : <Film size={14} />}
                  </span>
                  <span
                    style={{
                      fontSize: 13,
                      color: "var(--ql-fg)",
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {sourceLabel(s)}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      dispatch({ type: "patch", patch: { sources: state.sources.filter((_, idx) => idx !== i) } })
                    }
                    aria-label={`Remove ${sourceLabel(s)}`}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: "var(--ql-fg-subtle)",
                      cursor: "pointer",
                      display: "inline-flex",
                      padding: 4,
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                {/* The study's own sentence about THIS source, next to the
                    thing that needs fixing. */}
                {failed && (
                  <div style={{ fontSize: 12.5, color: "var(--ql-danger)", lineHeight: 1.5, paddingLeft: 24 }}>{failed}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {error && (
        <div style={{ fontSize: 13.5, color: "var(--ql-danger)", lineHeight: 1.6, whiteSpace: "pre-line" }}>{error}</div>
      )}
    </div>
  );
}
